import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/react';
import { CONDITIONS, buildCardTitle, isDuplicateCard } from './titleUtils';
const API_BASE = process.env.REACT_APP_API_URL || '';

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const MARKET_CURRENCY = { AU: 'AUD', US: 'USD', UK: 'GBP', CA: 'CAD', DE: 'EUR' };

function applyDetectionRules(data, rules) {
  if (!rules || rules.length === 0) return data;
  const result = { ...data };
  for (const rule of rules) {
    if (!rule.field || !rule.match) continue;
    const val = result[rule.field];
    if (typeof val === 'string' && val.toLowerCase().includes(rule.match.toLowerCase())) {
      result[rule.field] = rule.replace;
    }
  }
  return result;
}

function ReviewLayout({ theme, market, urls, onApprove, onApproveAndExport, resumedCards = [], initialResult = null, detectionRules = [], templates = [], approvedCards = [], onDuplicateSkip, onDetect, onEditsChange, onRescan, keysDisabled = false }) {
  const { getToken } = useAuth();
  const isMobile = useIsMobile();
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg     = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#eeecea';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';
  const green  = '#4a9b6f';
  const orange = '#c47c2b';

  const cards = [];
  for (let i = 0; i < urls.length; i += 2) {
    cards.push({ front: urls[i], back: urls[i + 1] || null });
  }

  const total = cards.length;

  // Map resumed cards to their batch position by front image URL — positions can
  // have gaps (skips, re-scans), so array order alone isn't reliable
  const resumedByIndex = {};
  resumedCards.forEach((rc, i) => {
    const pos = cards.findIndex(c => c.front === rc.frontUrl);
    resumedByIndex[pos !== -1 ? pos : i] = rc;
  });

  const [index, setIndex]           = useState(() => Math.min(resumedCards.length, Math.max(0, total - 1)));
  const [results, setResults]       = useState(() => {
    const init = {};
    Object.entries(resumedByIndex).forEach(([i, card]) => { init[i] = card; });
    if (initialResult?.index != null && initialResult?.data) {
      init[initialResult.index] = initialResult.data;
    }
    return init;
  });
  const [edits, setEdits]           = useState(() => {
    if (initialResult?.index != null && initialResult?.edits) {
      return { [initialResult.index]: initialResult.edits };
    }
    return {};
  });
  const [loading, setLoading]       = useState(false);
  const [detectionStep, setDetectionStep] = useState(0);
  const [rescans, setRescans]       = useState({});

  const [approved, setApproved]     = useState(() => {
    const init = {};
    Object.keys(resumedByIndex).forEach(i => { init[i] = true; });
    return init;
  });
  const [quantities, setQuantities] = useState(() => {
    const init = {};
    Object.entries(resumedByIndex).forEach(([i, card]) => { if (card.quantity) init[i] = card.quantity; });
    if (initialResult?.index != null && initialResult?.quantity != null) {
      init[initialResult.index] = initialResult.quantity;
    }
    return init;
  });
  const [subsetPrompt, setSubsetPrompt] = useState(null);
  // Duplicate handling: dupModal = { original, context: 'detected'|'approve'|'manual', pendingCard },
  // dupResolved[index] remembers the user's choice so we never re-prompt for the same card
  const [dupModal, setDupModal] = useState(null);
  const [dupResolved, setDupResolved] = useState({});
  const [showApprovedList, setShowApprovedList] = useState(false);
  // "Mark as duplicate" — manual override for when automatic detection misses
  // a real duplicate (e.g. a variant note typed in after the early check ran).
  // Opens a picker over approvedCards; selecting one reuses the same dupModal
  // confirmation flow as automatic detection, just with context: 'manual'.
  const [showMarkDuplicate, setShowMarkDuplicate] = useState(false);
  const [scpSearch, setScpSearch]             = useState('');
  const [scpSearching, setScpSearching]       = useState(false);
  const [scpSearchResults, setScpSearchResults] = useState(null);
  const [detectionTimes, setDetectionTimes]   = useState({});
  const [titleOverrides, setTitleOverrides]   = useState(() => {
    const init = {};
    Object.entries(resumedByIndex).forEach(([i, card]) => { if (card.titleOverride != null) init[i] = card.titleOverride; });
    return init;
  });
  const [face, setFace]                       = useState('front');
  const [lightboxOpen, setLightboxOpen]       = useState(false);
  const [gallerySize, setGallerySize]         = useState({ width: 0, height: 0 });
  const [leftWidth, setLeftWidth]             = useState(55);
  const isDragging = useRef(false);
  const containerRef = useRef(null);
  const card = cards[index];
  const abortRef = useRef(null);
  const prefetchedRef = useRef(new Set());
  const galleryRef = useRef(null);
  // True for the window between marking a card approved and its onApprove network
  // call actually resolving. approved[index] flips to true before that call
  // finishes, so a second Enter/click in that window would otherwise hit the
  // "already approved, go to next" path and advance the index a second time on
  // top of finalizeApprove's own advance once the pending call resolves.
  const approvingRef = useRef(false);

  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setGallerySize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!card?.back) return;
    const handleKey = (e) => {
      if (e.shiftKey && e.key === 'ArrowLeft') setFace('front');
      if (e.shiftKey && e.key === 'ArrowRight') setFace('back');
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [card?.back]); // eslint-disable-line react-hooks/exhaustive-deps


  // Natural portrait card width at full container height (5:7 ratio)
  const cardNaturalWidth = gallerySize.height > 0 ? Math.round(gallerySize.height * 5 / 7) : 0;
  const peekWidth = gallerySize.width > 0 ? gallerySize.width - cardNaturalWidth : 0;

  useEffect(() => {
    if (!card || results[index]) return;
    setLoading(true);
    setDetectionStep(1);
    setSubsetPrompt(null);
    setScpSearch('');
    setScpSearchResults(null);
    const controller = new AbortController();
    abortRef.current = controller;
    const t0 = Date.now();
    getToken().then(token => {
      fetch(`${API_BASE}/api/detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ frontUrl: card.front, backUrl: card.back }),
        signal: controller.signal,
      })
        .then(res => res.json())
        .then(rawData => {
          const data = applyDetectionRules(rawData, detectionRules);
          const elapsed = ((Date.now() - t0) / 1000).toFixed(1);
          setResults(prev => ({ ...prev, [index]: data }));
          setDetectionTimes(prev => ({ ...prev, [index]: elapsed }));
          setLoading(false);
          if (onDetect) onDetect(index, data);
          if (data.scp?.corrections?.possibleSubset) {
            setSubsetPrompt(data.scp.corrections.possibleSubset);
          }
          // Pre-fetch next card in background
          const nextIndex = index + 1;
          const nextCard = cards[nextIndex];
          if (nextCard && !prefetchedRef.current.has(nextIndex)) {
            prefetchedRef.current.add(nextIndex);
            const nt0 = Date.now();
            fetch(`${API_BASE}/api/detect`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
              body: JSON.stringify({ frontUrl: nextCard.front, backUrl: nextCard.back }),
            })
              .then(r => r.json())
              .then(rawNext => {
                const nextData = applyDetectionRules(rawNext, detectionRules);
                setResults(prev => prev[nextIndex] ? prev : { ...prev, [nextIndex]: nextData });
                setDetectionTimes(prev => ({ ...prev, [nextIndex]: ((Date.now() - nt0) / 1000).toFixed(1) }));
              })
              .catch(() => {});
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return;
          setResults(prev => ({ ...prev, [index]: { error: true } }));
          setLoading(false);
        });
    });
  }, [index, rescans[index]]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setScpSearch('');
    setScpSearchResults(null);
    setFace('front');
    setLightboxOpen(false);
  }, [index]);

  // Pre-fetch the card after the current one whenever index changes
  useEffect(() => {
    const nextIndex = index + 1;
    const nextCard = cards[nextIndex];
    if (!nextCard || prefetchedRef.current.has(nextIndex)) return;
    prefetchedRef.current.add(nextIndex);
    const nt0 = Date.now();
    getToken().then(token => {
      fetch(`${API_BASE}/api/detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ frontUrl: nextCard.front, backUrl: nextCard.back }),
      })
        .then(r => r.json())
        .then(rawNext => {
          const nextData = applyDetectionRules(rawNext, detectionRules);
          setResults(prev => prev[nextIndex] ? prev : { ...prev, [nextIndex]: nextData });
          setDetectionTimes(prev => ({ ...prev, [nextIndex]: ((Date.now() - nt0) / 1000).toFixed(1) }));
        })
        .catch(() => {});
    });
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) { setDetectionStep(0); return; }
    const s1 = setTimeout(() => setDetectionStep(2), 4000);
    const s2 = setTimeout(() => setDetectionStep(3), 8000);
    return () => { clearTimeout(s1); clearTimeout(s2); };
  }, [loading]);







  const getQuantity = () => quantities[index] ?? 1;
  const setQuantity = (val) => {
    const n = Math.max(1, Math.min(999, parseInt(val) || 1));
    setQuantities(prev => ({ ...prev, [index]: n }));
    if (onEditsChange) onEditsChange(index, edits[index] || {}, results[index] || null, n);
    // A card stays editable after approval, but nothing previously re-synced
    // those edits into approvedCards — adjusting qty on an already-approved
    // card silently had no effect on what actually got exported. Re-approve
    // in place (safe/idempotent: doApprove's countedRef guard means this
    // never double-counts usage or re-fires the approve network call).
    if (approved[index] && onApprove) onApprove(buildCardData(edits[index] || {}, n), index, total);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (!e.shiftKey) return;
      if (e.key === '=' || e.key === '+') setQuantity(Math.min(999, (quantities[index] ?? 1) + 1));
      if (e.key === '-' || e.key === '_') setQuantity(Math.max(1,   (quantities[index] ?? 1) - 1));
      if (e.key === 'Z' || e.key === 'z') setLightboxOpen(prev => !prev);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [index]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = results[index] || {};
  const edit   = edits[index]   || {};
  const scp    = result.scp     || null;

  // getValue checks edits first, then falls back to the raw detection result
  const getValue = (field) => edit[field] !== undefined ? edit[field] : (result[field] || '');

  // Find a previously-approved card matching the given fields (same physical
  // card at the same batch position never matches itself)
  const findDuplicate = (fields) =>
    approvedCards.find(a => a && a.frontUrl !== card?.front && isDuplicateCard(a, fields)) || null;

  // Early duplicate check — fires as soon as a detection result is on screen,
  // BEFORE the user spends time editing fields, price, or title. approvedCards
  // is in the dependency array as a safety net: if it updates a moment after
  // landing on this card (e.g. the previous card's approval was still in
  // flight over the network when we arrived here), this re-evaluates instead
  // of requiring the user to navigate away and back to get flagged.
  useEffect(() => {
    if (loading || !results[index] || results[index].error || results[index].skipped) return;
    if (approved[index] || dupResolved[index] || dupModal) return;
    const original = findDuplicate({
      player: getValue('player'), year: getValue('year'), season: getValue('year'),
      set: getValue('set'), cardNumber: getValue('cardNumber'),
      scp: results[index].scp || null,
    });
    if (original) setDupModal({ original, context: 'detected', pendingCard: null });
  }, [index, results[index], loading, approvedCards]); // eslint-disable-line react-hooks/exhaustive-deps

  const setValue = (field, value) => {
    const newEditsForCard = { ...(edits[index] || {}), [field]: value };
    setEdits(prev => ({ ...prev, [index]: newEditsForCard }));
    if (onEditsChange) onEditsChange(index, newEditsForCard, results[index] || null);
    // See setQuantity above — same re-sync, for every other field (price,
    // player, condition, etc.) edited after the card is already approved.
    if (approved[index] && onApprove) onApprove(buildCardData(newEditsForCard, getQuantity()), index, total);
  };

  // Title override is tracked in its own state (separate from edits), so it
  // needs the exact same post-approval re-sync as setValue/setQuantity above
  // — editing the eBay title textarea on an already-approved card previously
  // had no effect on what actually exported, same bug, different field.
  const setTitleOverride = (value) => {
    setTitleOverrides(prev => ({ ...prev, [index]: value }));
    if (approved[index] && onApprove) onApprove(buildCardData(edits[index] || {}, getQuantity(), value), index, total);
  };
  const resetTitleOverride = () => {
    setTitleOverrides(prev => { const n = { ...prev }; delete n[index]; return n; });
    if (approved[index] && onApprove) onApprove(buildCardData(edits[index] || {}, getQuantity(), null), index, total);
  };

  // Up to 4 distinct sell prices from the most recently approved cards,
  // newest first — approvedCards is appended to on each approval (doApprove
  // in App.js), so walking it backwards gives recency order for free.
  const recentPrices = [];
  for (let k = approvedCards.length - 1; k >= 0 && recentPrices.length < 4; k--) {
    const p = approvedCards[k]?.sellPrice;
    if (p && !recentPrices.includes(p)) recentPrices.push(p);
  }
  const mostRecentPrice = recentPrices[0] || '';

  // Apply a listing template (Settings → Listing Templates) to the current card
  // as edits — only fields the template actually sets are overwritten
  const applyTemplate = (tpl) => {
    if (!tpl) return;
    const newEdits = { ...(edits[index] || {}) };
    ['condition', 'sellPrice', 'sport', 'league'].forEach(k => { if (tpl[k]) newEdits[k] = tpl[k]; });
    setEdits(prev => ({ ...prev, [index]: newEdits }));
    if (onEditsChange) onEditsChange(index, newEdits, results[index] || null);
  };

  const applyCorrections = (corrections) => {
    const newEdits = { ...(edits[index] || {}) };
    if (corrections?.year)       newEdits.year       = corrections.year;
    if (corrections?.cardNumber) newEdits.cardNumber = corrections.cardNumber;
    if (corrections?.set)        newEdits.set        = corrections.set;
    if (corrections?.features)   newEdits.features   = corrections.features;
    if (corrections?.scpVariant) newEdits.scpVariant = corrections.scpVariant;
    setEdits(prev => ({ ...prev, [index]: newEdits }));
  };

  const applyMatchFromList = (match) => {
    const newEdits = { ...(edits[index] || {}) };

    // consoleName from server is already stripped of sport prefix (e.g. "Basketball Cards") by serializeProduct
    // e.g. "1992 Topps Archives" or "1993 Skybox Premium"
    const yearMatch = (match.consoleName || '').match(/\b(19\d{2}|20\d{2})\b/);
    if (yearMatch) {
      const y = parseInt(yearMatch[1]);
      newEdits.year = `${y}-${String(y + 1).slice(2)}`;
      // Set name is everything after the year in consoleName
      const afterYear = (match.consoleName || '').replace(/\b(19|20)\d{2}\b\s*/, '').trim();
      if (afterYear) newEdits.set = afterYear;
    }

    // Card number from productName e.g. "B.J. Armstrong #116" → "116"
    const numMatch = (match.productName || '').match(/#([A-Z0-9-]+)(\b|$)/i);
    if (numMatch) newEdits.cardNumber = numMatch[1];

    // Bracket variant e.g. "B.J. Armstrong [Gold] #116" → scpVariant = "Gold"
    const bracketMatch = (match.productName || '').match(/\[([^\]]+)\]/);
    if (bracketMatch && !/rookie/i.test(bracketMatch[1])) newEdits.scpVariant = bracketMatch[1].trim();
    else newEdits.scpVariant = ''; // clear any previous variant if this match has none

    // Rookie detection
    if (/rookie/i.test(match.productName || '')) newEdits.features = 'Rookie';

    // Re-apply detection rules so user rules (e.g. "Finest" → "Topps Finest")
    // are not undone by the raw SCP field values
    const ruledEdits = applyDetectionRules(newEdits, detectionRules);

    setEdits(prev => ({ ...prev, [index]: ruledEdits }));
    setSubsetPrompt(null);
    // Don't clear scpSearchResults — keeps original suggestions hidden after a search selection
  };

  const handleScpSearch = async () => {
    if (!scpSearch.trim()) return;
    setScpSearching(true);
    setScpSearchResults(null);
    try {
      const res = await fetch(`${API_BASE}/api/db-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: scpSearch.trim() })
      });
      const data = await res.json();
      setScpSearchResults(data.matches || []);
    } catch {
      setScpSearchResults([]);
    } finally {
      setScpSearching(false);
    }
  };

  // Wipes everything for the current card (result, edits, title override,
  // approved flag) and re-runs detection. Bumping rescans[index] re-fires the
  // detection effect even though index hasn't changed.
  const handleRescan = () => {
    if (loading) return;
    setResults(prev => { const next = { ...prev }; delete next[index]; return next; });
    setEdits(prev => { const next = { ...prev }; delete next[index]; return next; });
    setTitleOverrides(prev => { const next = { ...prev }; delete next[index]; return next; });
    setDetectionTimes(prev => { const next = { ...prev }; delete next[index]; return next; });
    setApproved(prev => { const next = { ...prev }; delete next[index]; return next; });
    setDupResolved(prev => { const next = { ...prev }; delete next[index]; return next; });
    setDupModal(null);
    setSubsetPrompt(null);
    setScpSearch('');
    setScpSearchResults(null);
    prefetchedRef.current.delete(index);
    if (onRescan) onRescan(index, card);
    setRescans(prev => ({ ...prev, [index]: (prev[index] || 0) + 1 }));
  };

  const handleRetry = handleRescan;

  const handleSkipDuringLoad = () => {
    if (abortRef.current) abortRef.current.abort();
    setLoading(false);
    setDetectionStep(0);
    setResults(prev => ({ ...prev, [index]: { skipped: true } }));
    setIndex(i => Math.min(total - 1, i + 1));
  };

  const handleSubsetKeep = () => {
    applyCorrections(scp?.corrections || {});
    setValue('subset', subsetPrompt);
    setSubsetPrompt(null);
  };

  const handleSubsetRemove = () => {
    applyCorrections(scp?.corrections || {});
    setSubsetPrompt(null);
  };

  const buildTitle = () => buildCardTitle({
    year:       getValue('year'),
    set:        getValue('set'),
    player:     getValue('player'),
    cardNumber: getValue('cardNumber'),
    features:   getValue('features'),
    subset:     getValue('subset'),
    scpVariant: getValue('scpVariant'),
    serialInfo: getValue('serialInfo'),
    team:       getValue('team'),
  });

  const getTitle = () => titleOverrides[index] !== undefined ? titleOverrides[index] : buildTitle();

  // Collect ALL fields — edits override raw result values. Takes explicit
  // edits/quantity/titleOverride overrides (rather than always reading
  // edits[index] / quantities[index] / titleOverrides[index] state) so
  // callers that just changed one of them via setEdits/setQuantities/
  // setTitleOverrides can build the fresh snapshot immediately, without
  // waiting a render for that state update to land.
  const buildCardData = (
    editsForCard = edits[index] || {},
    qty = getQuantity(),
    titleOverride = (titleOverrides[index] !== undefined ? titleOverrides[index] : null),
  ) => {
    const get = (field) => editsForCard[field] !== undefined ? editsForCard[field] : (result[field] || '');
    return {
      player:       get('player'),
      year:         get('year'),
      season:       get('year'),   // export uses both
      set:          get('set'),
      subset:       get('subset'),
      cardNumber:   get('cardNumber'),
      features:     get('features'),
      team:         get('team'),
      sport:        get('sport'),
      league:       get('league'),
      manufacturer: get('manufacturer'),
      scpVariant:   get('scpVariant'),
      serialInfo:   get('serialInfo'),
      sellPrice:    get('sellPrice') || mostRecentPrice,
      condition:    get('condition') || CONDITIONS[0].value,
      quantity:     qty,
      titleOverride,
      frontUrl:     card.front,
      backUrl:      card.back,
      scp:          scp || null,
    };
  };

  const handleApprove = async () => {
    if (approvingRef.current) return;
    const cardData = buildCardData();
    // Backstop duplicate check — catches cards edited into being duplicates
    // after the early (post-detection) check already passed, and also catches
    // the case where a user navigates back to a previously-skipped duplicate
    // and tries to approve it (dupResolved 'skipped' must NOT bypass this check;
    // only 'separate' — an explicit "keep as different card" choice — bypasses it).
    if (dupResolved[index] !== 'separate') {
      const original = findDuplicate(cardData);
      if (original) {
        setDupModal({ original, context: 'approve', pendingCard: cardData });
        return;
      }
    }
    await finalizeApprove(cardData);
  };

  // Awaits onApprove (App.js's doApprove, which records the approval over the
  // network) before advancing to the next card. Advancing first would let the
  // next card's early duplicate check run against a still-stale approvedCards
  // list — the exact bug this closes: scan two copies of a card back-to-back
  // and approve the first, and the second wouldn't be flagged as a duplicate
  // until you navigated away and back, by which point the network call had
  // caught up.
  const finalizeApprove = async (cardData) => {
    setApproved(prev => ({ ...prev, [index]: true }));
    approvingRef.current = true;
    try {
      if (onApprove) await onApprove(cardData, index, total);
    } finally {
      approvingRef.current = false;
    }
    if (index < total - 1) setIndex(i => i + 1);
  };

  // Shared by the "Next →" button and the Enter-key handler for an
  // already-approved card — guarded so it can't double-advance the index while
  // finalizeApprove's own advance for THIS card is still pending (see approvingRef).
  const goToNextCard = () => {
    if (approvingRef.current) return;
    setIndex(i => Math.min(total - 1, i + 1));
  };

  const handleDupSkip = (mergeImages = false) => {
    if (onDuplicateSkip) {
      const imagesToMerge = mergeImages ? [card.front, card.back].filter(Boolean) : null;
      onDuplicateSkip(dupModal.original.frontUrl, getQuantity(), index, total, imagesToMerge);
    }
    setDupResolved(prev => ({ ...prev, [index]: 'skipped' }));
    setDupModal(null);
    setIndex(i => Math.min(total - 1, i + 1));
  };

  const handleDupKeep = () => {
    const pending = dupModal.pendingCard;
    setDupResolved(prev => ({ ...prev, [index]: 'separate' }));
    setDupModal(null);
    if (pending) finalizeApprove(pending);
  };

  // Keyboard workflow — inactive while typing in a field, while a modal is open
  // over the review screen (keysDisabled from App), during detection, and in the
  // lightbox. Registered without deps to always see fresh state.
  useEffect(() => {
    const handleKey = (e) => {
      if (keysDisabled || loading || lightboxOpen || dupModal || showApprovedList || showMarkDuplicate) return;
      const tag = (e.target.tagName || '').toUpperCase();
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      // A focused button owns Enter (native click) but not arrows/digits —
      // otherwise the hotkeys go dead after any mouse click on a button
      if (tag === 'BUTTON' && e.key === 'Enter') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key === 'Enter') {
        e.preventDefault();
        if (approved[index]) goToNextCard();
        else handleApprove();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex(i => Math.max(0, i - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextCard();
      } else if (/^[1-9]$/.test(e.key)) {
        const list = scpSearchResults !== null
          ? scpSearchResults
          : (scp && (scp.status === 'multiple' || scp.status === 'suggestions') ? (scp.matches || []) : []);
        const match = list[parseInt(e.key) - 1];
        if (match) applyMatchFromList(match);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });

  const fields = [
    { key: 'year',         label: 'Year' },
    { key: 'set',          label: 'Set' },
    { key: 'player',       label: 'Player' },
    { key: 'subset',       label: 'Subset/Insert',      placeholder: 'None' },
    { key: 'cardNumber',   label: 'Card #' },
    { key: 'features',     label: 'Features' },
    { key: 'scpVariant',   label: 'Variant',     placeholder: 'None' },
    { key: 'team',         label: 'Team' },
    { key: 'sport',        label: 'Sport' },
    { key: 'league',       label: 'League' },
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'condition',    label: 'Condition', dropdown: true },
  ];
  // sellPrice is handled separately in Col 3

  const stepMessages = ['Reading card images...', 'Verifying details...', 'Checking card database...'];

  const isApproved = approved[index];

  // Batch progress, drawn as a fill behind the approved-count button. Skipped
  // cards never become approved cards, so a batch with skips deliberately tops
  // out below 100% rather than being fudged up to it.
  const approvedPct = total > 0 ? Math.min(100, (approvedCards.length / total) * 100) : 0;
  const approvedLabel = String(approvedCards.length).padStart(String(total).length, '0');
  const namedTemplates = templates.filter(t => t.name);

  const scpStatusColor = () => {
    if (!scp) return text2;
    if (scp.status === 'confirmed')  return green;
    if (scp.status === 'corrected')  return orange;
    if (scp.status === 'multiple' || scp.status === 'suggestions') return orange;
    return text2;
  };

  const scpStatusLabel = () => {
    if (!scp) return '';
    if (scp.status === 'confirmed')  return '✓ Database confirmed';
    if (scp.status === 'corrected')  return '⚠ Database suggests corrections';
    if (scp.status === 'multiple')   return '? Multiple matches found — select one';
    if (scp.status === 'suggestions') return '? Possible matches — select one';
    if (scp.status === 'not_found')  return '— Not found in database';
    return '';
  };



  const handleDividerMouseDown = (e) => {
    e.preventDefault();
    isDragging.current = true;
    const onMouseMove = (e) => {
      if (!isDragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftWidth(Math.min(Math.max(pct, 30), 70));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  return (
    <div ref={containerRef} style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', height: isMobile ? 'auto' : 'calc(100vh - 48px)', overflow: isMobile ? 'auto' : 'hidden' }}>

      {/* DETECTION MODAL */}
      {loading && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: '1px solid ' + border, borderRadius: 12, padding: '32px 40px', width: 360, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700, color: text, marginBottom: 4 }}>Bodega.</div>
              <div style={{ fontSize: 12, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>Card {index + 1} of {total}</div>
            </div>
            <div style={{ fontSize: 13, color: text }}>{stepMessages[detectionStep - 1] || stepMessages[0]}</div>
            <button
              onClick={handleSkipDuringLoad}
              style={{ alignSelf: 'center', padding: '6px 16px', background: 'transparent', border: '1px solid ' + border, borderRadius: 5, fontSize: 12, color: text2, cursor: 'pointer' }}>
              Skip card
            </button>
          </div>
        </div>
      )}

            {/* COL 1 — Fields + SCP */}
      <div style={{ width: isMobile ? '100%' : `${leftWidth}%`, borderRight: isMobile ? 'none' : 'none', borderBottom: isMobile ? '1px solid ' + border : 'none', padding: 20, overflowY: isMobile ? 'visible' : 'auto', order: isMobile ? 2 : 0, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: result?.error ? 8 : 16 }}>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: result?.error ? '#b94040' : text2, letterSpacing: '0.05em' }}>
            {loading ? 'DETECTING...' : result?.error ? 'DETECTION FAILED' : `DETECTED${detectionTimes[index] ? ` · ${detectionTimes[index]}s` : ''}`}
          </div>
          {!loading && !result?.error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setShowApprovedList(true)}
                title={`${approvedCards.length} of ${total} cards in this batch approved — click to view them`}
                style={{ position: 'relative', overflow: 'hidden', padding: '4px 10px', background: 'transparent', border: '1px solid ' + border, borderRadius: 4, fontSize: 10, color: approvedCards.length > 0 ? green : text2, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = green; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = border; }}>
                <span
                  aria-hidden="true"
                  style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${approvedPct}%`, background: theme === 'dark' ? 'rgba(74, 155, 111, 0.28)' : 'rgba(74, 155, 111, 0.20)', transition: 'width 300ms ease', pointerEvents: 'none' }} />
                <span style={{ position: 'relative' }}>✓ {approvedLabel}/{total} APPROVED</span>
              </button>
              {approvedCards.length > 0 && (
                <button
                  onClick={() => setShowMarkDuplicate(true)}
                  title="Mark this card as a duplicate of one you've already approved"
                  style={{ padding: '4px 10px', background: 'transparent', border: '1px solid ' + border, borderRadius: 4, fontSize: 10, color: text2, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = green; e.currentTarget.style.color = green; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = text2; }}>
                  ⚑ MARK DUPLICATE
                </button>
              )}
              {namedTemplates.length > 0 && (
                <select
                  value=""
                  onChange={e => {
                    const tpl = namedTemplates[parseInt(e.target.value)];
                    if (tpl) applyTemplate(tpl);
                    e.target.blur();
                  }}
                  title="Apply a listing template to this card"
                  style={{ height: 24, padding: '0 6px', background: 'transparent', border: '1px solid ' + border, borderRadius: 4, fontSize: 10, color: text2, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', maxWidth: 130 }}>
                  <option value="" disabled>✦ TEMPLATE</option>
                  {namedTemplates.map((t, i) => (
                    <option key={i} value={i}>{t.name}</option>
                  ))}
                </select>
              )}
              <button
                onClick={handleRescan}
                title="Discard the current result and re-scan this card from scratch"
                style={{ padding: '4px 10px', background: 'transparent', border: '1px solid ' + border, borderRadius: 4, fontSize: 10, color: text2, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = orange; e.currentTarget.style.color = orange; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = border; e.currentTarget.style.color = text2; }}>
                ↻ RE-SCAN
              </button>
            </div>
          )}
        </div>
        {result?.error && (
          <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 12, color: text2 }}>Something went wrong detecting this card.</div>
            <button onClick={handleRetry} style={{ padding: '5px 12px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 4, fontSize: 11, color: text2, cursor: 'pointer', whiteSpace: 'nowrap' }}>Try again</button>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 16px' }}>
          {fields.map(({ key, label, placeholder, dropdown }) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: text2, marginBottom: 4, fontFamily: 'IBM Plex Mono, monospace' }}>{label.toUpperCase()}</div>
              {dropdown ? (
                <select
                  value={getValue(key) || CONDITIONS[0].value}
                  onChange={e => setValue(key, e.target.value)}
                  disabled={loading}
                  style={{
                    width: '100%',
                    fontSize: 13,
                    color: text,
                    background: edit[key] !== undefined ? (theme === 'dark' ? '#1a2a1e' : '#f0f7f3') : bg2,
                    padding: '8px 10px',
                    borderRadius: 5,
                    border: '1px solid ' + (edit[key] !== undefined ? green : border),
                    outline: 'none',
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}>
                  {CONDITIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  value={getValue(key)}
                  onChange={e => setValue(key, e.target.value)}
                  disabled={loading}
                  placeholder={placeholder || ''}
                  style={{
                    width: '100%',
                    fontSize: 13,
                    color: text,
                    background: edit[key] !== undefined ? (theme === 'dark' ? '#1a2a1e' : '#f0f7f3') : bg2,
                    padding: '8px 10px',
                    borderRadius: 5,
                    border: '1px solid ' + (edit[key] !== undefined ? green : border),
                    outline: 'none',
                    fontFamily: 'IBM Plex Sans, sans-serif',
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          ))}
        </div>

        {/* SUBSET PROMPT */}
        {subsetPrompt && (
          <div style={{ marginBottom: 20, padding: 14, background: theme === 'dark' ? '#1e1a14' : '#fdf6ec', border: '1px solid ' + orange, borderRadius: 6 }}>
            <div style={{ fontSize: 12, color: orange, fontFamily: 'IBM Plex Mono, monospace', marginBottom: 8 }}>SUBSET DETECTED</div>
            <div style={{ fontSize: 13, color: text, marginBottom: 12 }}>
              AI detected "<strong>{subsetPrompt}</strong>" as part of the set name. Is this a subset/insert?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleSubsetKeep} style={{ padding: '7px 14px', background: orange, color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer', fontWeight: 500 }}>
                Yes, keep as subset
              </button>
              <button onClick={handleSubsetRemove} style={{ padding: '7px 14px', background: 'transparent', color: text2, border: '1px solid ' + border, borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                No, remove it
              </button>
            </div>
          </div>
        )}

        {/* SCP SECTION */}
        {!loading && scp && (
          <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid ' + border }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: scpStatusColor(), marginBottom: 12, letterSpacing: '0.05em' }}>
              DATABASE — {scpStatusLabel()}
            </div>

            {scp.status === 'confirmed' && (
              <div style={{ fontSize: 12, color: text2, padding: '8px 10px', background: bg2, borderRadius: 5, border: '1px solid ' + border }}>
                {scp.label}
              </div>
            )}

            {scp.status === 'corrected' && !subsetPrompt && (
              <div>
                <div style={{ fontSize: 12, color: text2, padding: '8px 10px', background: bg2, borderRadius: 5, border: '1px solid ' + border, marginBottom: 8 }}>
                  {scp.label}
                </div>
                <div style={{ fontSize: 11, color: orange, marginBottom: 8, lineHeight: 1.8 }}>
                  {scp.corrections?.year       && <div>Year: {result.year} → {scp.corrections.year}</div>}
                  {scp.corrections?.cardNumber && <div>Card #: {result.cardNumber} → {scp.corrections.cardNumber}</div>}
                  {scp.corrections?.set && !scp.corrections?.possibleSubset && <div>Set: {result.set} → {scp.corrections.set}</div>}
                  {scp.corrections?.features   && <div>Features: {result.features} → {scp.corrections.features}</div>}
                  {scp.corrections?.scpVariant && <div>Variant: [{scp.corrections.scpVariant}]</div>}
                </div>
                <button
                  onClick={() => { applyCorrections(scp.corrections); setSubsetPrompt(null); }}
                  style={{ fontSize: 11, padding: '6px 12px', background: orange, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  Apply corrections
                </button>
              </div>
            )}

            {scp.status === 'not_found' && !scpSearchResults && (
              <div style={{ fontSize: 12, color: text2, padding: '8px 10px', background: bg2, borderRadius: 5, border: '1px solid ' + border }}>
                No match found in database.
              </div>
            )}

            {(scp.status === 'multiple' || scp.status === 'suggestions') && !scpSearchResults && (
              <div>
                {(scp.matches || []).map((match, i) => (
                  <div
                    key={i}
                    onClick={() => applyMatchFromList(match)}
                    style={{
                      fontSize: 12,
                      color: text,
                      padding: '8px 10px',
                      background: bg2,
                      borderRadius: 5,
                      border: '1px solid ' + border,
                      marginBottom: 6,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = orange}
                    onMouseLeave={e => e.currentTarget.style.borderColor = border}
                  >
                    {match.label}
                  </div>
                ))}
              </div>
            )}

            {/* SCP SEARCH RESULTS — replaces original suggestions */}
            {scpSearchResults !== null && (
              <div style={{ marginBottom: 8 }}>
                {scpSearchResults.length === 0 ? (
                  <div style={{ fontSize: 12, color: text2, padding: '8px 10px', background: bg2, borderRadius: 5, border: '1px solid ' + border }}>
                    No results found.
                  </div>
                ) : (
                  scpSearchResults.map((match, i) => (
                    <div
                      key={i}
                      onClick={() => applyMatchFromList(match)}
                      style={{
                        fontSize: 12,
                        color: text,
                        padding: '8px 10px',
                        background: bg2,
                        borderRadius: 5,
                        border: '1px solid ' + border,
                        marginBottom: 6,
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = green}
                      onMouseLeave={e => e.currentTarget.style.borderColor = border}
                    >
                      {match.label}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* SCP MANUAL SEARCH */}
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid ' + border }}>
              <div style={{ fontSize: 10, color: text2, fontFamily: 'IBM Plex Mono, monospace', marginBottom: 8, letterSpacing: '0.05em' }}>SEARCH DATABASE</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={scpSearch}
                  onChange={e => setScpSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleScpSearch()}
                  placeholder='e.g. "B.J. Armstrong #132"'
                  style={{
                    flex: 1,
                    fontSize: 12,
                    color: text,
                    background: bg2,
                    padding: '7px 10px',
                    borderRadius: 5,
                    border: '1px solid ' + border,
                    outline: 'none',
                    fontFamily: 'IBM Plex Sans, sans-serif',
                  }}
                />
                <button
                  onClick={handleScpSearch}
                  disabled={scpSearching || !scpSearch.trim()}
                  style={{
                    padding: '7px 12px',
                    background: scpSearch.trim() ? text : bg2,
                    color: scpSearch.trim() ? bg : text2,
                    border: '1px solid ' + border,
                    borderRadius: 5,
                    fontSize: 12,
                    cursor: scpSearch.trim() ? 'pointer' : 'default',
                    whiteSpace: 'nowrap',
                  }}>
                  {scpSearching ? '...' : 'Search'}
                </button>
              </div>

            </div>

          </div>
        )}
      </div>

      {/* Drag divider */}
      {!isMobile && (
        <div
          onMouseDown={handleDividerMouseDown}
          style={{ width: 5, flexShrink: 0, cursor: 'col-resize', background: border, order: 0, transition: 'background 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = '#a0998f'}
          onMouseLeave={e => e.currentTarget.style.background = border}
        />
      )}

      {/* COL 2 — Images + Title + Price + Actions */}
      <div style={{ width: isMobile ? '100%' : undefined, flex: isMobile ? undefined : 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', order: isMobile ? 1 : 0, minWidth: 0 }}>

        {/* Gallery — overlapping card view */}
        {/* BACKUP (single card view):
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <div onClick={() => setLightboxOpen(true)} style={{ cursor: 'zoom-in', position: 'relative', height: '100%' }}>
            <img src={face === 'front' ? card.front : card.back} alt={face} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
            {card.back && (<><button onClick={e => { e.stopPropagation(); setFace(f => f === 'front' ? 'back' : 'front'); }} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff', fontSize: 20, width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>‹</button><button onClick={e => { e.stopPropagation(); setFace(f => f === 'front' ? 'back' : 'front'); }} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff', fontSize: 20, width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>›</button></>)}
            <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', padding: '3px 8px', borderRadius: 4, letterSpacing: '0.04em' }}>ZOOM</div>
          </div>
        </div>
        END BACKUP */}
        <div ref={galleryRef} style={{ flex: 1, minHeight: isMobile ? 280 : 0, position: 'relative', overflow: 'hidden' }}>

          {/* Front card — main on left, shrinks to peek when back active */}
          {cardNaturalWidth > 0 && (
            <div
              onClick={() => face === 'back' ? setFace('front') : setLightboxOpen(true)}
              style={{
                position: 'absolute', top: 0, bottom: 0, left: 0,
                width: face === 'front' ? (card.back ? cardNaturalWidth : gallerySize.width) : peekWidth,
                transition: 'width 0.35s ease',
                zIndex: face === 'front' ? 2 : 1,
                overflow: 'hidden',
                boxShadow: face === 'front' && card.back ? '8px 0 28px rgba(0,0,0,0.8)' : 'none',
                cursor: face === 'back' ? 'pointer' : 'zoom-in',
              }}
            >
              <img src={card.front} alt="front" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                objectFit: 'cover', display: 'block',
              }} />
            </div>
          )}

          {/* Back card — peeks on right at natural portrait width, expands to main */}
          {card.back && cardNaturalWidth > 0 && (
            <div
              onClick={() => face === 'front' ? setFace('back') : setLightboxOpen(true)}
              style={{
                position: 'absolute', top: 0, bottom: 0, right: 0,
                width: face === 'back' ? cardNaturalWidth : peekWidth,
                transition: 'width 0.35s ease',
                zIndex: face === 'back' ? 2 : 1,
                overflow: 'hidden',
                boxShadow: face === 'back' ? '-8px 0 28px rgba(0,0,0,0.8)' : 'none',
                cursor: face === 'front' ? 'pointer' : 'zoom-in',
              }}
            >
              <img src={card.back} alt="back" style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                objectFit: 'cover', display: 'block',
              }} />
            </div>
          )}

          {/* ZOOM + arrow hint labels */}
          <div style={{ position: 'absolute', bottom: 8, left: 8, display: 'flex', gap: 6, zIndex: 10, pointerEvents: 'none' }}>
            <div style={{ background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', padding: '3px 8px', borderRadius: 4, letterSpacing: '0.04em' }}>ZOOM</div>
            {card.back && <div style={{ background: 'rgba(0,0,0,0.4)', color: '#fff', fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', padding: '3px 8px', borderRadius: 4, letterSpacing: '0.04em' }}>← → to flip</div>}
          </div>

          {/* Arrow toggles */}
          {card.back && (
            <>
              <button
                onClick={e => { e.stopPropagation(); setFace(f => f === 'front' ? 'back' : 'front'); }}
                style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff', fontSize: 20, width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, zIndex: 10 }}
              >‹</button>
              <button
                onClick={e => { e.stopPropagation(); setFace(f => f === 'front' ? 'back' : 'front'); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', color: '#fff', fontSize: 20, width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1, zIndex: 10 }}
              >›</button>
            </>
          )}

        </div>

        {/* Lightbox */}
        {lightboxOpen && (
          <div
            onClick={() => setLightboxOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, cursor: 'zoom-out' }}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              style={{ position: 'absolute', top: 20, right: 20, background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, color: '#fff', fontSize: 18, cursor: 'pointer', width: 36, height: 36 }}>
              ×
            </button>
            {card.back && (
              <div style={{ position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                {['front', 'back'].map(f => (
                  <button
                    key={f}
                    onClick={e => { e.stopPropagation(); setFace(f); }}
                    style={{
                      padding: '4px 14px',
                      borderRadius: 20,
                      border: `1px solid ${face === f ? '#fff' : 'rgba(255,255,255,0.3)'}`,
                      background: face === f ? '#fff' : 'transparent',
                      color: face === f ? '#000' : '#fff',
                      fontSize: 11,
                      fontFamily: 'IBM Plex Mono, monospace',
                      cursor: 'pointer',
                      letterSpacing: '0.04em',
                    }}>
                    {f.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <img
              src={face === 'front' ? card.front : card.back}
              alt={face}
              onClick={e => e.stopPropagation()}
              style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }}
            />
          </div>
        )}

        {/* eBay title + price + actions */}
        <div style={{ padding: '12px 16px 16px', borderTop: '1px solid ' + border, display: 'flex', flexDirection: 'column', gap: 10, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text2, letterSpacing: '0.05em' }}>EBAY TITLE PREVIEW</div>
            {titleOverrides[index] !== undefined && (
              <button onClick={resetTitleOverride} style={{ background: 'none', border: 'none', fontSize: 10, color: orange, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', padding: 0 }}>↺ Reset</button>
            )}
          </div>
          <textarea
            value={loading ? 'Detecting...' : getTitle()}
            onChange={e => setTitleOverride(e.target.value)}
            disabled={loading}
            rows={2}
            style={{ fontSize: 12, color: text, background: bg2, padding: 10, borderRadius: 5, border: '1px solid ' + (titleOverrides[index] !== undefined ? orange : border), lineHeight: 1.6, width: '100%', resize: 'none', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }}
          />

          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: isApproved ? green : text2 }}>
            {isApproved ? '✓ Approved' : 'Card ' + (index + 1) + ' of ' + total}
          </div>

          {/* SELL PRICE + QUANTITY */}
          <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: text2, marginBottom: 6, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em' }}>SELL PRICE ({MARKET_CURRENCY[market] || 'AUD'})</div>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: text2 }}>$</span>
              <input
                value={getValue('sellPrice')}
                onChange={e => setValue('sellPrice', e.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="0.00"
                style={{
                  width: '100%',
                  fontSize: 13,
                  color: text,
                  background: getValue('sellPrice') ? (theme === 'dark' ? '#1a2a1e' : '#f0f7f3') : bg2,
                  padding: '8px 10px 8px 22px',
                  borderRadius: 5,
                  border: '1px solid ' + (getValue('sellPrice') ? green : border),
                  outline: 'none',
                  fontFamily: 'IBM Plex Mono, monospace',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            {recentPrices.length > 0 && (
              <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
                {recentPrices.map(p => (
                  <button
                    key={p}
                    onClick={() => setValue('sellPrice', p)}
                    title={`Use $${p}`}
                    style={{ padding: '3px 8px', borderRadius: 4, border: '1px solid ' + border, background: bg2, color: text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer' }}>
                    ${p}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ flex: '0 0 auto' }}>
            <div style={{ fontSize: 10, color: text2, marginBottom: 6, fontFamily: 'IBM Plex Mono, monospace', letterSpacing: '0.05em' }}>QTY</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <button
                onClick={() => setQuantity(getQuantity() - 1)}
                disabled={getQuantity() <= 1}
                style={{ width: 28, height: 34, background: bg2, border: '1px solid ' + border, borderRight: 'none', borderRadius: '5px 0 0 5px', fontSize: 16, color: text2, cursor: getQuantity() <= 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                −
              </button>
              <input
                type="number"
                min="1"
                max="999"
                value={getQuantity()}
                onChange={e => setQuantity(e.target.value)}
                style={{ width: 40, height: 34, textAlign: 'center', fontSize: 13, color: text, background: bg2, border: '1px solid ' + border, outline: 'none', fontFamily: 'IBM Plex Mono, monospace', boxSizing: 'border-box', MozAppearance: 'textfield' }}
              />
              <button
                onClick={() => setQuantity(getQuantity() + 1)}
                style={{ width: 28, height: 34, background: bg2, border: '1px solid ' + border, borderLeft: 'none', borderRadius: '0 5px 5px 0', fontSize: 16, color: text2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                +
              </button>
            </div>
          </div>
          </div>{/* end SELL PRICE + QTY flex */}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setIndex(i => Math.max(0, i - 1))} style={{ flex: 1, padding: isMobile ? '13px 0' : '10px 0', background: 'transparent', border: '1px solid ' + border, borderRadius: 5, fontSize: isMobile ? 14 : 12, color: text2, cursor: 'pointer' }}>← Back</button>
<button onClick={isApproved ? goToNextCard : handleApprove} style={{ flex: isMobile ? 2 : 1, padding: isMobile ? '13px 0' : '10px 0', background: text, border: 'none', borderRadius: 5, fontSize: isMobile ? 14 : 12, color: bg, fontWeight: 500, cursor: 'pointer' }}>{isApproved ? 'Next →' : 'Approve →'}</button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8 }}>
            {index < total - 1
              ? <button onClick={() => setIndex(i => i + 1)} style={{ padding: '8px 0', background: 'transparent', border: 'none', fontSize: 11, color: text2, cursor: 'pointer' }}>Skip card</button>
              : <span style={{ padding: '8px 0', fontSize: 11, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>Last card</span>
            }
            {!isApproved && (
              <>
                <span style={{ fontSize: 11, color: text2, lineHeight: 1 }}>|</span>
                <button onClick={() => {
                  setApproved(prev => ({ ...prev, [index]: true }));
                  if (onApproveAndExport) onApproveAndExport(buildCardData(), index);
                }} style={{ padding: '8px 0', background: 'transparent', border: 'none', fontSize: 11, color: text2, cursor: 'pointer' }}>Approve & Export</button>
              </>
            )}
          </div>
        </div>

      </div>

      {/* DUPLICATE MODAL — early (right after detection) or approve-time backstop */}
      {dupModal && (() => {
        const orig = dupModal.original;
        const early = dupModal.context === 'detected';
        const manual = dupModal.context === 'manual';
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
            <div style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 400, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: '#d97706', letterSpacing: '0.05em', marginBottom: 8 }}>{manual ? 'MARKED AS DUPLICATE' : 'DUPLICATE DETECTED'}</div>
                <div style={{ fontSize: 13, color: text, fontWeight: 500, marginBottom: 4 }}>
                  {getValue('player')} {getValue('year')} {getValue('set')} #{getValue('cardNumber')}
                </div>
                <div style={{ fontSize: 12, color: text2, lineHeight: 1.6 }}>
                  {early
                    ? <>Matches a card you've already approved (qty {orig?.quantity || 1}) — you can skip it now, before editing anything.</>
                    : <>This card was already approved earlier in this batch (qty {orig?.quantity || 1}).</>}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {dupResolved[index] !== 'skipped' && (
                <button
                  onClick={() => handleDupSkip(false)}
                  style={{ height: 38, borderRadius: 6, border: 'none', background: green, color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {manual ? 'Confirm' : 'Skip'} — add to existing qty (now {(orig?.quantity || 1) + getQuantity()})
                </button>
                )}
                {dupResolved[index] !== 'skipped' && (
                <button
                  onClick={() => handleDupSkip(true)}
                  title="Also adds this scan's front/back photos to the existing listing's images"
                  style={{ height: 38, borderRadius: 6, border: `1px solid ${green}`, background: 'transparent', color: green, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                  {manual ? 'Confirm' : 'Skip'} — add qty + images (now {(orig?.quantity || 1) + getQuantity()})
                </button>
                )}
                {!manual && (
                <button
                  onClick={handleDupKeep}
                  style={{ height: 38, borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 13, cursor: 'pointer' }}>
                  {early ? "Keep — it's a different card" : 'Approve anyway as separate listing'}
                </button>
                )}
                <button
                  onClick={() => setDupModal(null)}
                  style={{ height: 34, borderRadius: 6, border: 'none', background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* APPROVED CARDS LIST */}
      {showApprovedList && (
        <div onClick={() => setShowApprovedList(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 10, padding: 24, width: 460, maxHeight: '75vh', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>
                Approved cards <span style={{ color: green }}>({approvedCards.length})</span>
              </div>
              <button onClick={() => setShowApprovedList(false)} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {approvedCards.length === 0 ? (
              <div style={{ fontSize: 12, color: text2, fontStyle: 'italic', padding: '8px 0' }}>No cards approved yet.</div>
            ) : (
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {approvedCards.map((a, i) => {
                  const pos = cards.findIndex(c => c.front === a.frontUrl);
                  return (
                    <div
                      key={a.frontUrl || i}
                      onClick={() => { if (pos !== -1) { setIndex(pos); setShowApprovedList(false); } }}
                      title={pos !== -1 ? 'Jump to this card' : ''}
                      style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: bg2, border: `1px solid ${border}`, borderRadius: 6, cursor: pos !== -1 ? 'pointer' : 'default' }}
                      onMouseEnter={e => { if (pos !== -1) e.currentTarget.style.borderColor = green; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = border; }}>
                      {a.frontUrl && <img src={a.frontUrl} alt="" style={{ width: 34, height: 48, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.titleOverride || buildCardTitle(a)}
                        </div>
                        <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text2, marginTop: 2 }}>
                          {pos !== -1 ? `card ${pos + 1}` : ''}{(a.quantity || 1) > 1 ? ` · qty ${a.quantity}` : ''}{a.sellPrice ? ` · $${a.sellPrice}` : ''}
                        </div>
                      </div>
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: green, flexShrink: 0 }}>✓</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* MARK AS DUPLICATE — picker over approvedCards; selecting a row opens
          the dupModal confirmation flow above with context: 'manual' */}
      {showMarkDuplicate && (
        <div onClick={() => setShowMarkDuplicate(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 10, padding: 24, width: 460, maxHeight: '75vh', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>
                Mark as duplicate of…
              </div>
              <button onClick={() => setShowMarkDuplicate(false)} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            {approvedCards.length === 0 ? (
              <div style={{ fontSize: 12, color: text2, fontStyle: 'italic', padding: '8px 0' }}>No cards approved yet.</div>
            ) : (
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {approvedCards.map((a, i) => (
                  <div
                    key={a.frontUrl || i}
                    onClick={() => {
                      setShowMarkDuplicate(false);
                      setDupModal({ original: a, context: 'manual', pendingCard: null });
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 8, background: bg2, border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = green; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = border; }}>
                    {a.frontUrl && <img src={a.frontUrl} alt="" style={{ width: 34, height: 48, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, color: text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.titleOverride || buildCardTitle(a)}
                      </div>
                      <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text2, marginTop: 2 }}>
                        {(a.quantity || 1) > 1 ? `qty ${a.quantity}` : ''}{a.sellPrice ? `${(a.quantity || 1) > 1 ? ' · ' : ''}$${a.sellPrice}` : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

export default ReviewLayout;
